/**
 * leaveBalanceExcel.js — "Balanca e pushimeve" si libër Excel i gatshëm për HR.
 *
 * Ekrani shfaq pesëmbëdhjetë kolona dhe katër prej tyre i mban të fshehura;
 * këtu shkruhet ÇDO fushë që kthen `GET /api/reports/leave-balance`, ndaj
 * skedari është dosja e plotë edhe kur tabela nuk është.
 *
 * Asnjë formulë pushimi nuk rillogaritet këtu: çdo ditë është numri që e ka
 * llogaritur backend-i (`backend/services/leaveSummary.js`). E vetmja aritmetikë
 * është mbledhja e atyre numrave — totali i fletës dhe përmbledhja sipas
 * departamentit — që çdo tabelë të mbyllet me rreshtat e shtypur pranë saj.
 * Formulat janë dokumentuar te `docs/notes/leave_balance_report.md`.
 *
 * Fletët: Balanca · Sipas departamentit · Mospërputhjet (kur ka) · Shpjegime.
 */
import * as XLSX from 'xlsx-js-style';

/* ─── Paleta — e njëjta blu si PDF-ja e këtij raporti (utils/pdfPrint.js) ──── */
const BRAND      = '1D4ED8';
const BRAND_DARK = '1E3A5F';
const INK        = '334155';
const INK_STRONG = '0F172A';
const MUTED      = '64748B';
const ZEBRA      = 'F8FAFC';
const WARN_BG    = 'FEF3C7';
const GRID       = 'E2E8F0';

const FILL   = (rgb) => ({ patternType: 'solid', fgColor: { rgb } });
const BORDER = (rgb = GRID) => ({
  top:    { style: 'thin', color: { rgb } },
  bottom: { style: 'thin', color: { rgb } },
  left:   { style: 'thin', color: { rgb } },
  right:  { style: 'thin', color: { rgb } },
});

const S = {
  company: { font: { bold: true, sz: 11, color: { rgb: MUTED } } },
  title:   { font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND),
             alignment: { horizontal: 'left', vertical: 'center', indent: 1 } },
  sub:     { font: { bold: true, sz: 10, color: { rgb: INK_STRONG } } },
  meta:    { font: { italic: true, sz: 9, color: { rgb: MUTED } } },
  section: { font: { bold: true, sz: 10, color: { rgb: INK_STRONG } }, fill: FILL('EEF2F6'),
             alignment: { vertical: 'center', indent: 1 } },
  band:    { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND_DARK),
             alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER('FFFFFF') },
  head:    { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND),
             alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: BORDER('B9C7E0') },
  kpiVal:  { font: { bold: true, sz: 13, color: { rgb: INK_STRONG } },
             alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER() },
  note:    { font: { sz: 9, color: { rgb: INK } }, alignment: { vertical: 'top', wrapText: true } },
};

/* ─── Formatet numerike — Excel-i i mban numrat numra, jo tekst ───────────── */
const NUMFMT = {
  int:      '0',
  days:     '0.##',
  signed:   '+0.##;-0.##;0',
  date:     'dd.mm.yyyy',
  datetime: 'dd.mm.yyyy hh:mm',
};
const NUMERIC = new Set(['int', 'days', 'signed']);

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Datë-vetëm si datë lokale — `new Date('2019-03-01')` është mesnatë UTC dhe
 *  në zonat perëndim të Greenwich-it bie një ditë prapa. */
function toDate(v) {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d;
}
function toDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d;
}
/** Qelizë boshe, jo `null`: aoa_to_sheet e kapërcen `null`-in dhe rreshti do ta
 *  humbte kornizën/ngjyrën në atë kolonë. */
const numOrBlank = (v) => (v === null || v === undefined || v === '' ? '' : Number(v) || 0);
const txtOrBlank = (v) => (v === null || v === undefined ? '' : String(v));

const DISCREPANCY_FIELD = { used: 'Ditët e përdorura', pending: 'Ditët në pritje' };

const discrepancyText = (row) => (row.discrepancies || [])
  .map((d) => `${DISCREPANCY_FIELD[d.field] || d.field}: ruajtur ${d.stored} · nga kërkesat ${d.fromRequests}`)
  .join(' · ');

/* ─── Modeli i kolonave — i plotë, pavarësisht çfarë shfaq tabela ─────────── */
const COLS = [
  { g: 'IDENTITETI',  label: 'Nr.',                w: 5,  type: 'int',      pick: (r, i) => i + 1 },
  { g: 'IDENTITETI',  label: 'ID',                 w: 6,  type: 'int',      pick: (r) => numOrBlank(r.workerId) },
  { g: 'IDENTITETI',  label: 'Punonjësi',          w: 26, type: 'text',     pick: (r) => txtOrBlank(r.workerName) },
  { g: 'IDENTITETI',  label: 'Departamenti',       w: 20, type: 'text',     pick: (r) => txtOrBlank(r.department) },
  { g: 'IDENTITETI',  label: 'Pozita',             w: 22, type: 'text',     pick: (r) => txtOrBlank(r.position) },
  { g: 'IDENTITETI',  label: 'Data e fillimit',    w: 13, type: 'date',     pick: (r) => toDate(r.hireDate) },
  { g: 'IDENTITETI',  label: 'Statusi',            w: 9,  type: 'text',     align: 'center', pick: (r) => (r.active === false ? 'Joaktiv' : 'Aktiv') },
  { g: 'IDENTITETI',  label: 'Vite shërbimi',      w: 8,  type: 'int',      pick: (r) => numOrBlank(r.yearsOfService) },

  { g: 'PERIUDHA',    label: 'Viti',               w: 7,  type: 'int',      pick: (r) => numOrBlank(r.year) },
  { g: 'PERIUDHA',    label: 'Prej',               w: 12, type: 'date',     pick: (r) => toDate(r.periodStart) },
  { g: 'PERIUDHA',    label: 'Deri',               w: 12, type: 'date',     pick: (r) => toDate(r.periodEnd) },
  { g: 'PERIUDHA',    label: 'Ditë të punësuara',  w: 10, type: 'int',      pick: (r) => numOrBlank(r.employedDaysInYear) },
  { g: 'PERIUDHA',    label: 'Vit i pjesshëm',     w: 10, type: 'text',     align: 'center', pick: (r) => (r.isPartialYear ? 'Po' : 'Jo') },

  { g: 'TË DREJTAT (ditë)', label: 'Bazë vjetore',        w: 10, type: 'days',   total: 'baseAnnualDays',  pick: (r) => numOrBlank(r.baseAnnualDays) },
  { g: 'TË DREJTAT (ditë)', label: 'Proporcionale',       w: 11, type: 'days',   total: 'proRataDays',     pick: (r) => numOrBlank(r.proRataDays) },
  { g: 'TË DREJTAT (ditë)', label: 'Bartur',              w: 9,  type: 'days',   total: 'carriedOverDays', pick: (r) => numOrBlank(r.carriedOverDays) },
  { g: 'TË DREJTAT (ditë)', label: 'Shtesë',              w: 9,  type: 'days',   total: 'extraDays',       pick: (r) => numOrBlank(r.extraDays) },
  { g: 'TË DREJTAT (ditë)', label: 'Korrigjim ±',         w: 10, type: 'signed', total: 'adjustmentDays',  pick: (r) => numOrBlank(r.adjustmentDays) },
  { g: 'TË DREJTAT (ditë)', label: 'Të drejtat gjithsej', w: 12, type: 'days',   total: 'entitlementDays', strong: true, pick: (r) => numOrBlank(r.entitlementDays) },

  { g: 'SHFRYTËZIMI (ditë)', label: 'Aprovuar',   w: 10, type: 'days', total: 'approvedDays',  pick: (r) => numOrBlank(r.approvedDays) },
  { g: 'SHFRYTËZIMI (ditë)', label: 'Përdorur',   w: 10, type: 'days', total: 'usedDays',      pick: (r) => numOrBlank(r.usedDays) },
  { g: 'SHFRYTËZIMI (ditë)', label: 'Në pritje',  w: 10, type: 'days', total: 'pendingDays',   pick: (r) => numOrBlank(r.pendingDays) },
  { g: 'SHFRYTËZIMI (ditë)', label: 'Mbetur',     w: 10, type: 'days', total: 'remainingDays', emphasis: true, pick: (r) => numOrBlank(r.remainingDays) },
  { g: 'SHFRYTËZIMI (ditë)', label: 'Kërkesa',    w: 8,  type: 'int',  total: 'requestCount',  pick: (r) => numOrBlank(r.requestCount) },

  { g: 'VERIFIKIMI',  label: 'Përdorur (ruajtur)',  w: 13, type: 'days', pick: (r) => numOrBlank(r.storedUsedDays) },
  { g: 'VERIFIKIMI',  label: 'Në pritje (ruajtur)', w: 13, type: 'days', pick: (r) => numOrBlank(r.storedPendingDays) },
  { g: 'VERIFIKIMI',  label: 'Mospërputhje',        w: 11, type: 'text', align: 'center', pick: (r) => (r.hasDiscrepancy ? 'Po' : 'Jo') },
  { g: 'VERIFIKIMI',  label: 'Detajet e mospërputhjes', w: 42, type: 'text', wrap: true, pick: discrepancyText },
  { g: 'VERIFIKIMI',  label: 'Përditësuar',         w: 16, type: 'datetime', pick: (r) => toDateTime(r.lastUpdatedAt) },
];

const NC = COLS.length;

/** Stili i një qelize të trupit — vija, zebra, theksimi i "Mbetur". */
function bodyStyle(col, { zebra, warn, negative }) {
  const font = { sz: 9, color: { rgb: INK } };
  if (col.strong)   { font.bold = true; font.color = { rgb: INK_STRONG }; }
  if (col.emphasis) { font.bold = true; font.color = { rgb: negative ? 'B91C1C' : '15803D' }; }
  const s = {
    font,
    alignment: {
      vertical: 'center',
      horizontal: col.align || (NUMERIC.has(col.type) ? 'right' : col.type === 'date' || col.type === 'datetime' ? 'center' : 'left'),
      wrapText: !!col.wrap,
    },
    border: BORDER(),
  };
  if (warn) s.fill = FILL(WARN_BG);
  else if (zebra) s.fill = FILL(ZEBRA);
  return s;
}

/** Mbledhje e thjeshtë e ditëve të llogaritura nga backend-i (pa formula). */
function sumRows(rows, fields) {
  const acc = {};
  fields.forEach((f) => { acc[f] = 0; });
  rows.forEach((r) => { fields.forEach((f) => { acc[f] += Number(r[f]) || 0; }); });
  fields.forEach((f) => { acc[f] = r2(acc[f]); });
  return acc;
}

const SUM_FIELDS = [
  'baseAnnualDays', 'proRataDays', 'carriedOverDays', 'extraDays', 'adjustmentDays',
  'entitlementDays', 'approvedDays', 'usedDays', 'pendingDays', 'remainingDays', 'requestCount',
];

/** Pivot sipas departamentit — mbledhje e po atyre numrave, asgjë e re. */
function byDepartment(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.department || 'Pa departament';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return [...map.entries()]
    .map(([department, list]) => ({ department, workers: list.length, ...sumRows(list, SUM_FIELDS) }))
    .sort((a, b) => a.department.localeCompare(b.department, 'sq'));
}

/* ─── Ndërtuesi i fletës ──────────────────────────────────────────────────── */
function newSheetBuilder() {
  const aoa = [];
  const merges = [];
  const ops = [];
  const heights = [];
  return {
    aoa, merges, ops, heights,
    push: (row = []) => { aoa.push(row); return aoa.length - 1; },
    style: (r, c, s, z) => ops.push({ r, c, s, z }),
    styleRow: (r, s, cols) => cols.forEach((c) => ops.push({ r, c, s })),
    merge: (r, from, to) => merges.push({ s: { r, c: from }, e: { r, c: to } }),
  };
}

function finishSheet(b, { widths, autofilter }) {
  const ws = XLSX.utils.aoa_to_sheet(b.aoa);
  ws['!cols']   = widths.map((wch) => ({ wch }));
  ws['!merges'] = b.merges;
  ws['!rows']   = b.heights;
  if (autofilter) ws['!autofilter'] = { ref: autofilter };
  b.ops.forEach(({ r, c, s, z }) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) return;
    if (s) ws[addr].s = s;
    if (z) ws[addr].z = z;
  });
  return ws;
}

const pad = (row, n = NC) => {
  const out = row.slice();
  while (out.length < n) out.push('');
  return out;
};

/* ─── Fleta 1: Balanca ────────────────────────────────────────────────────── */
function sheetDetail(rows, { year, company, filterLine, stamp, totals }) {
  const b = newSheetBuilder();
  const last = NC - 1;

  const companyR = b.push([company || 'Nexuma']);
  b.merge(companyR, 0, last);
  b.style(companyR, 0, S.company);

  const titleR = b.push([`Balanca e pushimeve vjetore — ${year}`]);
  b.merge(titleR, 0, last);
  b.style(titleR, 0, S.title);
  b.heights[titleR] = { hpt: 26 };

  const subR = b.push([`Periudha: 01.01.${year} – 31.12.${year}  ·  Punonjës në raport: ${rows.length}`]);
  b.merge(subR, 0, last);
  b.style(subR, 0, S.sub);

  const filtR = b.push([filterLine]);
  b.merge(filtR, 0, last);
  b.style(filtR, 0, S.meta);

  const stampR = b.push([`Gjeneruar: ${stamp}  ·  Burimi: GET /api/reports/leave-balance (backend/services/leaveSummary.js)`]);
  b.merge(stampR, 0, last);
  b.style(stampR, 0, S.meta);

  b.push([]);

  /* Rripi i treguesve */
  const kpiSecR = b.push(['PËRMBLEDHJE']);
  b.merge(kpiSecR, 0, last);
  b.style(kpiSecR, 0, S.section);

  const KPI = [
    ['Punonjës',      rows.length,               'int'],
    ['Të drejtat',    totals.entitlementDays,    'days'],
    ['Aprovuar',      totals.approvedDays,       'days'],
    ['Përdorur',      totals.usedDays,           'days'],
    ['Në pritje',     totals.pendingDays,        'days'],
    ['Mbetur',        totals.remainingDays,      'days'],
    ['Kërkesa',       totals.requestCount,       'int'],
    ['Me mospërputhje', rows.filter((r) => r.hasDiscrepancy).length, 'int'],
  ];
  const kpiHeadR = b.push(pad(KPI.map((k) => k[0])));
  const kpiValR  = b.push(pad(KPI.map((k) => k[1])));
  KPI.forEach((k, c) => {
    b.style(kpiHeadR, c, S.head);
    b.style(kpiValR,  c, S.kpiVal, NUMFMT[k[2]]);
  });
  b.heights[kpiValR] = { hpt: 20 };

  b.push([]);

  /* Banda e grupeve mbi kokën e tabelës */
  const bandR = b.push(pad(COLS.map((c, i) => (i === 0 || COLS[i - 1].g !== c.g ? c.g : ''))));
  let from = 0;
  COLS.forEach((c, i) => {
    b.style(bandR, i, S.band);
    const isLast = i === NC - 1 || COLS[i + 1].g !== c.g;
    if (isLast) { if (i > from) b.merge(bandR, from, i); from = i + 1; }
  });

  const headR = b.push(COLS.map((c) => c.label));
  COLS.forEach((_, c) => b.style(headR, c, S.head));
  b.heights[headR] = { hpt: 30 };

  /* Rreshtat */
  rows.forEach((row, i) => {
    const r = b.push(COLS.map((c) => c.pick(row, i)));
    const warn = !!row.hasDiscrepancy;
    const negative = Number(row.remainingDays) < 0;
    COLS.forEach((c, ci) => b.style(r, ci, bodyStyle(c, { zebra: i % 2 === 1, warn, negative }), NUMFMT[c.type]));
  });

  const lastDataR = b.aoa.length - 1;

  /* Totali — mbledhje e rreshtave të mësipërm, që fleta të mbyllet vetë */
  const totalStyle = (col) => ({
    font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } },
    fill: FILL(BRAND_DARK),
    alignment: { vertical: 'center', horizontal: col.align || (NUMERIC.has(col.type) ? 'right' : 'left'), indent: col.align ? 0 : 1 },
    border: BORDER(BRAND_DARK),
  });
  const totalR = b.push(COLS.map((c, i) => {
    if (i === 0) return 'TOTALI';
    if (i === 2) return `${rows.length} punonjës`;
    return c.total ? totals[c.total] : '';
  }));
  COLS.forEach((c, ci) => b.style(totalR, ci, totalStyle(c), NUMFMT[c.type]));
  b.heights[totalR] = { hpt: 20 };

  const ref = rows.length
    ? `${XLSX.utils.encode_cell({ r: headR, c: 0 })}:${XLSX.utils.encode_cell({ r: lastDataR, c: NC - 1 })}`
    : null;

  return finishSheet(b, { widths: COLS.map((c) => c.w), autofilter: ref });
}

/* ─── Fleta 2: Sipas departamentit ────────────────────────────────────────── */
function sheetByDepartment(rows, { year, stamp, totals }) {
  const HEAD = ['Departamenti', 'Punonjës', 'Të drejtat', 'Aprovuar', 'Përdorur', 'Në pritje', 'Mbetur', 'Kërkesa'];
  const FIELDS = [null, 'workers', 'entitlementDays', 'approvedDays', 'usedDays', 'pendingDays', 'remainingDays', 'requestCount'];
  const WIDTHS = [28, 10, 12, 11, 11, 11, 11, 10];
  const b = newSheetBuilder();
  const last = HEAD.length - 1;

  const titleR = b.push([`Balanca e pushimeve ${year} — sipas departamentit`]);
  b.merge(titleR, 0, last);
  b.style(titleR, 0, S.title);
  b.heights[titleR] = { hpt: 24 };

  const metaR = b.push([`Mbledhje e rreshtave të fletës "Balanca"  ·  Gjeneruar: ${stamp}`]);
  b.merge(metaR, 0, last);
  b.style(metaR, 0, S.meta);
  b.push([]);

  const headR = b.push(HEAD);
  HEAD.forEach((_, c) => b.style(headR, c, S.head));
  b.heights[headR] = { hpt: 26 };

  byDepartment(rows).forEach((d, i) => {
    const r = b.push(FIELDS.map((f, c) => (c === 0 ? d.department : d[f])));
    FIELDS.forEach((f, c) => {
      const col = { type: c === 0 ? 'text' : c === 1 ? 'int' : c === 7 ? 'int' : 'days', emphasis: c === 6 };
      b.style(r, c, bodyStyle(col, { zebra: i % 2 === 1, negative: Number(d.remainingDays) < 0 }), NUMFMT[col.type]);
    });
  });

  const totalR = b.push(['TOTALI', rows.length, totals.entitlementDays, totals.approvedDays,
    totals.usedDays, totals.pendingDays, totals.remainingDays, totals.requestCount]);
  HEAD.forEach((_, c) => b.style(totalR, c, {
    font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } },
    fill: FILL(BRAND_DARK),
    alignment: { vertical: 'center', horizontal: c === 0 ? 'left' : 'right', indent: c === 0 ? 1 : 0 },
    border: BORDER(BRAND_DARK),
  }, c === 0 ? undefined : NUMFMT[c === 1 || c === 7 ? 'int' : 'days']));
  b.heights[totalR] = { hpt: 20 };

  return finishSheet(b, { widths: WIDTHS });
}

/* ─── Fleta 3: Mospërputhjet ──────────────────────────────────────────────── */
function sheetDiscrepancies(rows, { stamp }) {
  const HEAD = ['Punonjësi', 'Departamenti', 'Fusha', 'Ruajtur te leave_balances', 'Nga kërkesat', 'Diferenca'];
  const WIDTHS = [26, 20, 20, 22, 14, 12];
  const b = newSheetBuilder();
  const last = HEAD.length - 1;

  const titleR = b.push(['Mospërputhjet mes numëruesve të ruajtur dhe kërkesave']);
  b.merge(titleR, 0, last);
  b.style(titleR, 0, S.title);
  b.heights[titleR] = { hpt: 24 };

  const noteR = b.push(['Kolonat e raportit tregojnë gjithmonë vlerën e llogaritur nga kërkesat — kërkesat janë burimi i vërtetë. '
    + `Numëruesit e ruajtur duhen rikonstruktuar.  ·  Gjeneruar: ${stamp}`]);
  b.merge(noteR, 0, last);
  b.style(noteR, 0, S.meta);
  b.push([]);

  const headR = b.push(HEAD);
  HEAD.forEach((_, c) => b.style(headR, c, S.head));
  b.heights[headR] = { hpt: 26 };

  let i = 0;
  rows.forEach((row) => {
    (row.discrepancies || []).forEach((d) => {
      const diff = r2((Number(d.fromRequests) || 0) - (Number(d.stored) || 0));
      const r = b.push([row.workerName || '', row.department || '',
        DISCREPANCY_FIELD[d.field] || d.field, Number(d.stored) || 0, Number(d.fromRequests) || 0, diff]);
      [0, 1, 2, 3, 4, 5].forEach((c) => b.style(
        r, c,
        bodyStyle({ type: c >= 3 ? 'days' : 'text', emphasis: c === 5 }, { zebra: i % 2 === 1, negative: diff < 0 }),
        c >= 3 ? NUMFMT.days : undefined,
      ));
      i += 1;
    });
  });

  return finishSheet(b, { widths: WIDTHS });
}

/* ─── Fleta 4: Shpjegime ──────────────────────────────────────────────────── */
const GLOSSARY = [
  ['Bazë vjetore',        'Ditët vjetore të lejuara: leave_balances.accrued_days, ose cilësimi i kompanisë (settings.annual_leave) kur punonjësi s\'ka rresht balance për atë vit.'],
  ['Proporcionale',       'Bazë vjetore × (ditët kalendarike të punësimit brenda vitit ÷ ditët e vitit), rrumbullakuar në 2 dhjetore. E barabartë me bazën kur punonjësi ka qenë i punësuar tërë vitin; 0 kur s\'ka qenë fare.'],
  ['Bartur',              'Ditët e mbartura nga viti paraprak (leave_balances.carried_over).'],
  ['Shtesë',              'Bonusi i vjetërsisë: floor(vite të plota shërbimi ÷ 5) × 1 ditë — neni 32 i Ligjit të Punës së Kosovës. Përvjetori duhet të ketë kaluar.'],
  ['Korrigjim ±',         'Korrigjim manual i HR-së me shenjë (leave_balances.adjustment_days): shtesë ose zbritje mbi të drejtën e llogaritur.'],
  ['Të drejtat gjithsej', 'Proporcionale + Shtesë + Bartur + Korrigjim ±.'],
  ['Aprovuar',            'Ditët e aprovuara gjithsej për vitin = të përdorurat + të aprovuarat që s\'kanë ardhur ende. Llogariten nga vetë kërkesat, jo nga numëruesit e ruajtur.'],
  ['Përdorur',            'Ditët e aprovuara që kanë përfunduar para datës së sotme. Janë brenda "Aprovuar", ndaj nuk zbriten dy herë.'],
  ['Në pritje',           'Ditët e kërkuara që presin vendim (pending_supervisor / pending_hr). Zbriten sepse janë të zëna.'],
  ['Mbetur',              'Të drejtat gjithsej − Aprovuar − Në pritje. Nuk klampohet në zero: një balancë e tejshpenzuar shfaqet e kuqe sepse është problem i të dhënave.'],
  ['Kërkesa',             'Numri i kërkesave të pushimit vjetor të atij punonjësi për vitin.'],
  ['Vit i pjesshëm',      '"Po" kur punonjësi ka filluar ose ka përfunduar punën brenda vitit — atëherë e drejta llogaritet proporcionalisht.'],
  ['Mospërputhje',        'Numëruesit e ruajtur te leave_balances ndryshojnë nga shifrat e llogaritura nga kërkesat reale. Raporti tregon gjithmonë vlerën nga kërkesat.'],
  ['Përditësuar',         'leave_balances.updated_at — kur është prekur së fundmi rreshti i balancës.'],
  ['Llojet që numërohen', 'Vetëm pushimi vjetor (lloji 1) dhe pushimi me pagesë (lloji 6). Mjekësori, pa pagesë, martesori, jashtëzakonshmi dhe dhurimi i gjakut raportohen veçmas.'],
];

function sheetGlossary({ stamp }) {
  const b = newSheetBuilder();

  const titleR = b.push(['Shpjegimi i kolonave dhe formulat']);
  b.merge(titleR, 0, 1);
  b.style(titleR, 0, S.title);
  b.heights[titleR] = { hpt: 24 };

  const metaR = b.push([`Burimi i vetëm i kalkulimeve: backend/services/leaveSummary.js  ·  docs/notes/leave_balance_report.md  ·  ${stamp}`]);
  b.merge(metaR, 0, 1);
  b.style(metaR, 0, S.meta);
  b.push([]);

  const headR = b.push(['Kolona', 'Kuptimi / Formula']);
  [0, 1].forEach((c) => b.style(headR, c, S.head));
  b.heights[headR] = { hpt: 22 };

  GLOSSARY.forEach(([term, meaning], i) => {
    const r = b.push([term, meaning]);
    b.style(r, 0, { ...S.note, font: { ...S.note.font, bold: true, color: { rgb: INK_STRONG } }, border: BORDER(), fill: i % 2 ? FILL(ZEBRA) : undefined });
    b.style(r, 1, { ...S.note, border: BORDER(), fill: i % 2 ? FILL(ZEBRA) : undefined });
    b.heights[r] = { hpt: Math.max(16, Math.ceil(meaning.length / 95) * 14 + 6) };
  });

  return finishSheet(b, { widths: [24, 110] });
}

/* ─── Hyrja publike ───────────────────────────────────────────────────────── */
/**
 * @param {object}   input
 * @param {Array}    input.rows      rreshtat e raportit, ashtu si i ka kthyer backend-i
 * @param {number}   input.year      viti i zgjedhur
 * @param {string}   [input.company] emri i kompanisë për kokën
 * @param {string}   [input.filterLine] përshkrimi njërreshtësh i filtrave aktivë
 * @param {Date}     [input.generatedAt]
 * @returns {string} emri i skedarit të shkarkuar
 */
export function exportLeaveBalanceExcel({ rows = [], year, company = '', filterLine = '', generatedAt = new Date() } = {}) {
  const stamp  = generatedAt.toLocaleString('sq-AL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const totals = sumRows(rows, SUM_FIELDS);
  const ctx    = { year, company, filterLine, stamp, totals };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheetDetail(rows, ctx), 'Balanca');
  XLSX.utils.book_append_sheet(wb, sheetByDepartment(rows, ctx), 'Sipas departamentit');

  const flagged = rows.filter((r) => r.hasDiscrepancy);
  if (flagged.length) XLSX.utils.book_append_sheet(wb, sheetDiscrepancies(flagged, ctx), 'Mospërputhjet');

  XLSX.utils.book_append_sheet(wb, sheetGlossary(ctx), 'Shpjegime');

  wb.Props = {
    Title: `Balanca e pushimeve ${year}`,
    Subject: 'Balanca vjetore e pushimeve sipas punonjësit',
    Company: company || undefined,
    Author: 'Nexuma',
  };

  const filename = `balanca-pushimeve-${year}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}

export default exportLeaveBalanceExcel;
